import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { IProductCard } from '@models/IProductCard';
import CrossSellProductCTAStatusEnum from '@models/enums/CrossSellProductCTAStatusEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import classNames from 'classnames';

interface Props extends IProductCard {
  readonly ctaStatus: CrossSellProductCTAStatusEnum;
  readonly addToBasketLabel: string;
  readonly addedLabel: string;
  readonly removeLabel: string;
  readonly onAddToCart: (code: string) => void;
  readonly onRemoveFromCart: (code: string) => void;
  readonly isLightMode?: boolean;
  readonly dataTestId?: string;
  readonly className?: string;
}

const CrossSellProductCard = ({
  name,
  pricingData,
  learnMoreLink,
  sku,
  statusLabels,
  thumbnail,
  vatLabel,
  trackingData,
  ctaStatus,
  addToBasketLabel,
  addedLabel,
  removeLabel,
  onAddToCart,
  onRemoveFromCart,
  isLightMode,
  dataTestId = 'Cross-Sell-Product-Card',
  className,
}: Props) => {
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  return (
    <div
      data-product={trackingDataJson}
      className={classNames(
        'flex h-full flex-col rounded-[10px] bg-white px-2.5 pb-5 pt-2.5 text-black-100',
        className
      )}
      data-testid={dataTestId}
    >
      {thumbnail && (
        <div className="mb-5 h-[170px] md:h-[152px]">
          <a
            href={learnMoreLink?.url}
            target={learnMoreLink?.target}
            aria-label={name}
            // eslint-disable-next-line react/no-unknown-property
            track-product-click="true"
            data-product={trackingDataJson}
            data-testid={`${dataTestId}-Image-Anchor`}
          >
            <Image
              image={thumbnail}
              imageClasses="object-contain h-full"
              data-testid={`${dataTestId}-Image`}
            />
          </a>
        </div>
      )}

      {statusLabels && (
        <div
          className="body-four mb-[5px] text-center text-grey-450"
          data-testid={`${dataTestId}-Stock-Label`}
        >
          {pricingData.status === ProductStockStatusEnum.InStock &&
            statusLabels.inStockLabel}

          {pricingData.status === ProductStockStatusEnum.PreOrder &&
            statusLabels.preOrderLabel}

          {pricingData.status === ProductStockStatusEnum.BackOrder && (
            <>
              <span className="text-red">{statusLabels.outOfStockLabel}</span>{' '}
              <span className="text-grey-400">•</span>{' '}
              {statusLabels.availableToOrderLabel}
            </>
          )}
        </div>
      )}

      <div className="body-one mb-5 text-center">{name}</div>

      {pricingData?.formattedCalculatedPrice && (
        <PriceDisplayText
          pricingData={pricingData}
          vatLabel={vatLabel}
          centerText
          containerClassName="mb-5"
          taxesClassName="text-grey-450"
          taxesTopMarginClassName="mt-[5px]"
          data-testid={`${dataTestId}-Price`}
        />
      )}

      <div className="mt-auto flex items-center justify-center px-2.5">
        <Button
          data-testid={`${dataTestId}-CTA`}
          {...(ctaStatus === CrossSellProductCTAStatusEnum.Added ||
          ctaStatus === CrossSellProductCTAStatusEnum.Remove
            ? {
                secondary: true,
                secondaryBackgroundClassName: 'bg-yellow-200',
              }
            : {})}
          className={classNames('w-full', {
            'pointer-events-none':
              ctaStatus === CrossSellProductCTAStatusEnum.Added,
          })}
          onClick={() => {
            if (ctaStatus === CrossSellProductCTAStatusEnum.AddToBasket)
              onAddToCart(sku ?? '');

            if (ctaStatus === CrossSellProductCTAStatusEnum.Remove)
              onRemoveFromCart(sku ?? '');
          }}
          isLightMode={isLightMode}
        >
          {ctaStatus === CrossSellProductCTAStatusEnum.AddToBasket &&
            addToBasketLabel}

          {ctaStatus === CrossSellProductCTAStatusEnum.Added && (
            <div className="flex items-center justify-center gap-x-2.5">
              <Icon name="check" size="1.125rem" />
              <span className="cta">{addedLabel}</span>
            </div>
          )}

          {ctaStatus === CrossSellProductCTAStatusEnum.Remove && removeLabel}
        </Button>
      </div>
    </div>
  );
};

export default CrossSellProductCard;
